import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronDown, CreditCard, X, AlertCircle } from 'lucide-react';
import { clientApi } from '../api';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: resetea el estado de validación/planes cuando el teléfono queda incompleto.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const COSMIC_PLANS = [
  { id: 'solar', name: 'Solar', price: 597.00, benefits: '100GB, Redes Ilimitadas, Llamadas/SMS, Comunidad, Arte Tokenizado, Launchpad', originalPrice: 428.98 },
  { id: 'earth', name: 'Earth', price: 374.00, benefits: '40GB, Redes Ilimitadas, Llamadas/SMS, Mina Crypto, Comunidad, Planta un árbol', originalPrice: 268.49 },
  { id: 'lunar', name: 'Lunar', price: 251.00, benefits: '15GB, Redes Ilimitadas, Llamadas/SMS, Comparte Internet, Mina Crypto', originalPrice: 188.23 },
  { id: 'star', name: 'Star', price: 155.00, benefits: '5GB, Redes Ilimitadas, Llamadas/SMS, Comparte Internet, Mina Crypto', originalPrice: 124.03 },
  { id: 'galaxy', name: 'Galaxy', price: 150.00, benefits: '15GB Best Effort + 5GB Reducida, Redes Ilimitadas, 1500 SMS, No Comparte Internet', originalPrice: 111.09 },
  { id: 'comet', name: 'Comet', price: 129.00, benefits: '6GB Best Effort, Redes Ilimitadas, Llamadas, 1500 SMS, Comparte Internet', originalPrice: 95.05 },
  { id: 'gravity', name: 'Gravity', price: 80.00, benefits: '10GB Best Effort, Redes Ilimitadas, Llamadas/SMS Ilimitadas, No Comparte Internet', originalPrice: 55.56 },
  { id: 'sky', name: 'Sky', price: 55.00, benefits: '2GB Best Effort + 2GB Reducida, 375 Min, 125 SMS, Redes, No Comparte Internet', originalPrice: 32.80 },
];

export const CosmicPurchases = ({ onClose, mainPanelWidth }) => {
  const { t } = useTranslation('client');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(COSMIC_PLANS[0]);
  const [rechargeStatus, setRechargeStatus] = useState('idle'); // idle, loading, success, error

  // Calculate position: right edge of this panel attaches to left edge of main panel
  const rightOffset = mainPanelWidth || 600;
  const panelWidth = 450;

  useEffect(() => {
    if (phoneNumber.length === 10) {
      validateNumber(phoneNumber);
    } else {
      setIsValid(false);
      setShowPlans(false);
      setRechargeStatus('idle');
    }
  }, [phoneNumber]);

  const validateNumber = async (num) => {
    setIsValidating(true);
    setRechargeStatus('idle');
    try {
      const data = await clientApi.validateCosmicLine(num);
      if (data.valid) {
        setIsValid(true);
        setShowPlans(true);
      } else {
        setIsValid(false);
        setShowPlans(false);
      }
    } catch (e) {
      console.error('Error validating number:', e);
      setIsValid(false);
      setShowPlans(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRecharge = async () => {
    setRechargeStatus('loading');

    // Simulate API call to Addinteli for now (user asked for frontend UI first)
    setTimeout(() => {
      // randomly succeed or fail for demo
      if (Math.random() > 0.1) {
        setRechargeStatus('success');
      } else {
        setRechargeStatus('error');
      }
    }, 2000);
  };

  return ReactDOM.createPortal(
    <div
      className="cosmic-purchases-panel"
      style={{
        position: 'fixed',
        top: 0,
        right: rightOffset, // Attaches to the left of CosmicClient
        bottom: 0,
        width: panelWidth,
        zIndex: 999998, // just under the main panel's resizer if any
        background: '#18181b',
        borderLeft: '1px solid rgba(255,255,255,0.08)',
        borderRight: '1px solid rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-10px 0 30px rgba(0,0,0,0.5)',
        animation: 'slidePurchasesFromRight 0.3s cubic-bezier(0.16,1,0.3,1)'
      }}
    >
      <div className="cp-header">
        <div className="cp-header-top">
          <h2>{t('cosmicPurchases.title')}</h2>
          <button className="cp-close-btn" onClick={onClose}><X size={20} /></button>
        </div>
        <p className="cp-subtitle">{t('cosmicPurchases.subtitle')}</p>
      </div>

      <div className="cp-body">
        {/* Phone Input Wizard Step */}
        <div className="cp-input-group">
          <label>{t('cosmicPurchases.numberLabel')}</label>
          <div className={`cp-phone-input-wrapper ${isValid ? 'valid' : ''} ${isValidating ? 'validating' : ''}`}>
            <div className="cp-country-code">
              <span className="cp-flag">🇲🇽</span>
              <span>+52</span>
            </div>
            <input
              type="text"
              maxLength="10"
              placeholder={t('cosmicPurchases.numberPlaceholder')}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
            />
            {isValid && <CheckCircle2 className="cp-valid-icon" size={20} color="#22c55e" />}
          </div>
        </div>

        {/* Plans Wizard Step */}
        {showPlans && (
          <div className="cp-plans-section fade-in">
            <label>{t('cosmicPurchases.planLabel')}</label>
            <div className="cp-plan-select-wrapper">
              <select
                className="cp-plan-select"
                value={selectedPlan.id}
                onChange={(e) => setSelectedPlan(COSMIC_PLANS.find(p => p.id === e.target.value))}
              >
                {COSMIC_PLANS.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ${plan.price.toFixed(2)} MXN
                  </option>
                ))}
              </select>
              <ChevronDown className="cp-select-icon" size={16} />
            </div>

            <div className="cp-purple-box">
              <div className="cp-pb-header">
                <h3>{selectedPlan.name}</h3>
                <span className="cp-pb-price">${selectedPlan.price.toFixed(2)}</span>
              </div>
              <ul className="cp-pb-benefits">
                {selectedPlan.benefits.split(', ').map((benefit, i) => (
                  <li key={i}>
                    <CheckCircle2 size={14} color="#a855f7" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Sheet */}
      <div className={`cp-bottom-sheet ${showPlans ? 'active' : ''}`}>
        {rechargeStatus === 'success' ? (
          <div className="cp-status-msg success">
            <CheckCircle2 size={32} />
            <h4>{t('cosmicPurchases.successTitle')}</h4>
            <p>{t('cosmicPurchases.successMessage')}</p>
            <button className="cp-btn primary" onClick={onClose}>{t('cosmicPurchases.close')}</button>
          </div>
        ) : rechargeStatus === 'error' ? (
          <div className="cp-status-msg error">
            <AlertCircle size={32} />
            <h4>{t('cosmicPurchases.errorTitle')}</h4>
            <p>{t('cosmicPurchases.errorMessage')}</p>
            <button className="cp-btn outline" onClick={() => setRechargeStatus('idle')}>{t('cosmicPurchases.retry')}</button>
          </div>
        ) : (
          <div className="cp-action-content">
            <div className="cp-summary-row">
              <span>{t('cosmicPurchases.totalToPay')}</span>
              <span className="cp-total-price">${selectedPlan.price.toFixed(2)}</span>
            </div>
            <button
              className="cp-btn primary"
              onClick={handleRecharge}
              disabled={!isValid || rechargeStatus === 'loading'}
            >
              {rechargeStatus === 'loading' ? t('cosmicPurchases.processing') : t('cosmicPurchases.recharge')}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
